import ButtonSize from '@/Enums/ButtonSize.enum';
import { DownloadButton } from '@/components/Buttons/DownloadButton';
import ResetAudienceIcon from '@/components/Icons/ResetAudienceIcon';
import UserGroupIcon from '@/components/Icons/UserGroupIcon';
import { useAppSelector } from '@/store/hooks';
import { selectIsRequestAudienceButtonHidden } from '@/store/selectors/currentWhitelabel.selectors';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { type MouseEvent, useState } from 'react';

// Keep action labels on a single line; prevent the flex row from squeezing them.
const actionButtonSx = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

interface IProps {
  onResetAudience: () => void;
  onRequestAudiences: () => void;
  downloadButtonDisabled?: boolean;
  onDownload?: () => void;
  templatesEnabled?: boolean;
  onCreateTemplate?: () => void;
  onViewTemplates?: () => void;
}

const AudienceHeader = ({
  onResetAudience,
  onRequestAudiences,
  downloadButtonDisabled,
  onDownload,
  templatesEnabled,
  onCreateTemplate,
  onViewTemplates,
}: IProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isRequestAudienceButtonHidden = useAppSelector(selectIsRequestAudienceButtonHidden);

  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleCreate = () => {
    closeMenu();
    onCreateTemplate?.();
  };

  const handleView = () => {
    closeMenu();
    onViewTemplates?.();
  };

  return (
    <div className='flex w-full py-5 px-4 justify-between border-b'>
      <div className='flex flex-1 gap-3 items-center justify-start'>
        <UserGroupIcon />
        <h3 className='text-lg text-[#2E2C34] font-semibold'>Audience</h3>
      </div>
      <div className='flex flex-1 items-center justify-end w-full gap-3 print:hidden'>
        {templatesEnabled && (
          <>
            <Button
              onClick={openMenu}
              variant='outlined'
              endIcon={<KeyboardArrowDown />}
              sx={{ ...actionButtonSx, px: 2.5 }}
            >
              Templates
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
              {onCreateTemplate && <MenuItem onClick={handleCreate}>Create New Template</MenuItem>}
              <MenuItem onClick={handleView}>View Templates</MenuItem>
            </Menu>
          </>
        )}
        {!isRequestAudienceButtonHidden && (
          <Button onClick={onRequestAudiences} variant='contained' sx={actionButtonSx}>
            Request Audiences
          </Button>
        )}
        <Button
          onClick={onResetAudience}
          sx={{
            ...actionButtonSx,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          variant='outlined'
          startIcon={<ResetAudienceIcon />}
        >
          Reset Audience
        </Button>
        {onDownload && (
          <DownloadButton size={ButtonSize.Medium} disabled={!!downloadButtonDisabled} onClick={onDownload} />
        )}
      </div>
    </div>
  );
};

export default AudienceHeader;
